import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, Package, Truck, CheckCircle2, MapPin, Flame, Clock, LogOut, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";

/**
 * Delivery Driver View · Section 2.
 *
 * Mobile-first operational screen. Three tabs: Available / Mine / Completed.
 * Each delivery shows only what the driver needs: first name, phone, address,
 * item summary, total. The customer's email/notes are hidden. Server enforces
 * ownership once claimed; the UI stays polling.
 */
export default function DriverView() {
  const navigate = useNavigate();
  const [bucket, setBucket] = useState({ available: [], mine: [], completed_today: [], can_claim: false });
  const [tab, setTab] = useState("mine");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/driver/deliveries?include_completed=true");
      setBucket(data);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  const act = async (id, path, body) => {
    setBusyId(id);
    try {
      await api.post(`/driver/deliveries/${id}${path}`, body || {});
      await load();
      toast.success("Updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusyId(null);
    }
  };

  const current = bucket[tab] || [];

  return (
    <div className="min-h-screen bg-black text-white max-w-xl mx-auto">
      <div className="sticky z-20 backdrop-blur bg-black/80 border-b border-white/10 px-4 py-3 flex items-center justify-between" style={{ top: "calc(env(safe-area-inset-top, 0px) + 56px)" }}>
        <div>
          <h1 className="text-xl font-black">Driver</h1>
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{bucket.mine.length} active · auto-refresh 8s</p>
        </div>
        <button onClick={load} className="w-10 h-10 grid place-items-center rounded-full bg-white/5 hover:bg-white/10" data-testid="driver-refresh">
          <RotateCcw className="w-4 h-4" />
        </button>
      </div>

      <div className="px-4 pt-3 flex gap-1">
        {[
          { k: "mine", label: `Mine (${bucket.mine.length})`, color: "bg-pink-500" },
          bucket.can_claim && { k: "available", label: `Available (${bucket.available.length})`, color: "bg-emerald-500" },
          { k: "completed", label: `Today (${bucket.completed_today.length})`, color: "bg-zinc-700" },
        ].filter(Boolean).map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k === "completed" ? "completed_today" : t.k)}
            className={`flex-1 h-10 rounded-full text-sm font-semibold ${
              tab === (t.k === "completed" ? "completed_today" : t.k)
                ? `${t.color} text-white`
                : "bg-white/5 text-zinc-300"
            }`}
            data-testid={`driver-tab-${t.k}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="p-4 space-y-3">
        {current.length === 0 && (
          <div className="text-center py-16 text-zinc-500">
            {tab === "available" ? "No deliveries waiting right now." : tab === "mine" ? "No deliveries assigned to you yet." : "No deliveries completed today."}
          </div>
        )}
        {current.map((d) => (
          <div key={d.order_id} className="rounded-2xl bg-white/[0.04] border border-white/10 p-4 space-y-3" data-testid={`driver-card-${d.order_id}`}>
            <div className="flex items-center justify-between">
              <span className="font-bold">#{d.short_code}</span>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase ${
                d.status === "ready" ? "bg-emerald-500/15 text-emerald-200 border border-emerald-500/30" :
                d.status === "out_for_delivery" ? "bg-violet-500/15 text-violet-200 border border-violet-500/30" :
                "bg-zinc-700 text-zinc-300"
              }`}>
                {d.status === "ready" ? <Package className="w-3 h-3" /> : d.status === "out_for_delivery" ? <Truck className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
                {d.status.replace(/_/g, " ")}
              </span>
            </div>

            {d.vendor_name && (
              <p className="text-[11px] text-zinc-500 -mt-2">{d.vendor_name}{d.location_label ? ` · ${d.location_label}` : ""}</p>
            )}
            <div className="flex items-center gap-2 text-sm">
              <span className="font-semibold">{d.customer_first_name}</span>
              {d.contact_phone && (
                <a href={`tel:${d.contact_phone}`} className="ml-auto flex items-center gap-1 text-pink-300 text-xs" data-testid={`driver-call-${d.order_id}`}>
                  <Phone className="w-3 h-3" /> Call
                </a>
              )}
            </div>

            {d.delivery_address && (
              <div className="flex items-start gap-2 text-sm">
                <MapPin className="w-4 h-4 text-pink-300 mt-0.5 shrink-0" />
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(d.delivery_address)}`}
                  target="_blank" rel="noreferrer"
                  className="text-zinc-200 underline-offset-2 hover:underline"
                >
                  {d.delivery_address}
                </a>
              </div>
            )}

            <div className="space-y-0.5">
              {d.items.map((it, idx) => (
                <div key={idx} className="text-xs">
                  <span className="text-pink-300 font-bold">×{it.quantity}</span> {it.name}
                  {it.notes && <span className="text-zinc-500"> · {it.notes}</span>}
                </div>
              ))}
            </div>

            <div className="pt-2 border-t border-white/5 flex items-center justify-between">
              <span className="text-[10px] text-zinc-500 uppercase tracking-wider">
                {d.status === "ready" ? <>Ready {d.ready_at && `· ${new Date(d.ready_at).toLocaleTimeString()}`}</> : d.pickup_at ? `Picked ${new Date(d.pickup_at).toLocaleTimeString()}` : ""}
              </span>
              {d.total_cents != null && <span className="font-bold text-sm">${(d.total_cents / 100).toFixed(2)}</span>}
            </div>

            {/* Actions */}
            {["accepted", "preparing"].includes(d.status) && (
              <div className="w-full h-11 rounded-full bg-white/5 text-zinc-400 text-sm font-semibold grid place-items-center" data-testid={`driver-waiting-${d.order_id}`}>
                <span className="inline-flex items-center gap-1"><Clock className="w-4 h-4" /> Kitchen is preparing</span>
              </div>
            )}
            {d.status === "ready" && !d.driver_id && bucket.can_claim && (
              <Button
                disabled={busyId === d.order_id}
                onClick={() => act(d.order_id, "/claim")}
                className="w-full h-11 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold"
                data-testid={`driver-claim-${d.order_id}`}
              >
                <Package className="w-4 h-4 mr-1" /> Claim this
              </Button>
            )}
            {d.status === "ready" && d.driver_id && (
              <Button
                disabled={busyId === d.order_id}
                onClick={() => act(d.order_id, "/pickup", { note: "" })}
                className="w-full h-11 rounded-full bg-pink-500 hover:bg-pink-400 text-white font-bold"
                data-testid={`driver-pickup-${d.order_id}`}
              >
                <Truck className="w-4 h-4 mr-1" /> Picked up — on the way
              </Button>
            )}
            {d.status === "out_for_delivery" && (
              <Button
                disabled={busyId === d.order_id}
                onClick={() => act(d.order_id, "/complete", { note: "" })}
                className="w-full h-11 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold"
                data-testid={`driver-complete-${d.order_id}`}
              >
                <CheckCircle2 className="w-4 h-4 mr-1" /> Delivered
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
